import type { Customer } from '../../pages/cardex/api/useCustomer';
import type { Message } from '../../pages/cardex/api/useMessage';

import {
    ACTION_TYPE_EMAIL,
    ACTION_TYPE_LIVE_CHAT,
    ACTION_TYPE_SMS,
    ACTION_TYPE_WHATSAPP,
    canalDe,
} from '../config/canaux';
import type { Canal, CleCanal } from '../config/canaux';
import { t } from '../i18n/langue';

import Text from './Text';

/**
 * Le bandeau de canaux — #16, greffe de la variante B.
 *
 * **Permanent, au-dessus de la saisie.** Un clic par canal. Il remplace la
 * pastille « Répondre par … » et la feuille modale de la variante A : la bascule
 * de canal ne se cache pas derrière un geste.
 *
 * **Limité aux canaux joignables.** Un onglet barré n'a rien à faire au pouce, et
 * c'est le fil rouge de #19 : le composeur ne montre que ce qui aboutit. Avec un
 * seul canal joignable, le bandeau devient une **mention statique** — un onglet
 * unique cliquable dans le vide serait un contrôle qui ne contrôle rien.
 *
 * Le repli de canal est **nommé, jamais automatique** (#19 §5). Basculer d'office,
 * c'est décider à la place de l'hôtelier d'écrire sur un support que la cliente
 * n'a pas choisi — et pour le SMS, dépenser un crédit sans le dire.
 */

/** Les canaux sur lesquels le composeur sait écrire, dans l'ordre d'affichage. */
const CANAUX_COMPOSABLES: { cle: CleCanal; actionTypeId: number }[] = [
    { cle: 'whatsapp', actionTypeId: ACTION_TYPE_WHATSAPP },
    { cle: 'email', actionTypeId: ACTION_TYPE_EMAIL },
    { cle: 'sms', actionTypeId: ACTION_TYPE_SMS },
    { cle: 'live_chat', actionTypeId: ACTION_TYPE_LIVE_CHAT },
];

/**
 * Ce qui rend un canal joignable — mesuré par #19 sur les 1 068 clientes ayant
 * écrit en WhatsApp : **1 060 ont un téléphone (99 %)**, **854 un e-mail (80 %)**.
 * Le live chat n'est joignable que si le fil en contient déjà : il n'existe pas
 * d'adresse de live chat, seulement une session ouverte.
 */
export function canauxJoignables(client: Customer | undefined, messages: Message[]): Canal[] {
    if (!client) return [];

    return CANAUX_COMPOSABLES.filter(({ cle }) => {
        if (cle === 'email') return Boolean(client.email);
        if (cle === 'sms' || cle === 'whatsapp') return Boolean(client.phone);
        return messages.some((m) => m.action_type_id === ACTION_TYPE_LIVE_CHAT);
    }).map(({ actionTypeId }) => canalDe(actionTypeId));
}

interface BandeauCanauxProps {
    canaux: Canal[];
    actif: CleCanal;
    onChange: (cle: CleCanal) => void;
}

export default function BandeauCanaux({ canaux, actif, onChange }: BandeauCanauxProps) {
    if (canaux.length === 0) return null;

    // Un seul joignable : une mention, pas un onglet. Rien à choisir.
    if (canaux.length === 1) {
        const seul = canaux[0];
        return (
            <div className="flex items-center justify-center gap-2 border-b border-border-color-light bg-white py-2">
                <seul.Icone className="h-4 w-4 text-text-color-light" aria-hidden="true" />
                <Text as="span" variante="petit" ton="attenue">
                    {t('composeur.bandeau.seulCanal', { canal: seul.libelle })}
                </Text>
            </div>
        );
    }

    return (
        <div
            role="group"
            aria-label={t('composeur.bandeau.canalReponse')}
            className="flex border-b border-border-color-light bg-white"
        >
            {canaux.map((canal) => {
                const estActif = canal.cle === actif;

                return (
                    // L'état actif se lit sur le soulignement, pas sur un fond : le
                    // Button du socle imposerait sa variante.
                    // mobile-check-ignore — onglet de canal.
                    <button
                        key={canal.cle}
                        type="button"
                        aria-pressed={estActif}
                        onClick={() => onChange(canal.cle)}
                        className={`flex min-h-touch flex-1 items-center justify-center gap-1.5 border-b-2 px-2 ${
                            estActif
                                ? 'border-main-color text-main-color'
                                : 'border-transparent text-text-color-light'
                        }`}
                    >
                        <canal.Icone className="h-4 w-4" aria-hidden="true" />
                        <Text as="span" variante="petit">
                            {canal.libelle}
                        </Text>
                    </button>
                );
            })}
        </div>
    );
}
